<template>
  <div
    class="w-full overflow-hidden rounded-l-xl flex items-center justify-center"
    style="background: #edf2f7"
  >
    <div
      class="flex w-full sticky top-14 h-[calc(100vh-theme(spacing.2))] overflow-hidden"
    >
      <!-- Sidebar -->
      <div class="w-1/4 bg-white border-r border-gray-300">
        <!-- Sidebar Header -->
        <header
          class="p-4 border-b border-gray-300 flex justify-between items-center bg-oxford-blue text-white"
        >
          <h1 class="text-2xl font-semibold">Chat Web</h1>
          <div class="relative">
            <button id="menuButton" class="focus:outline-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-gray-100"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path d="M2 10a2 2 0 012-2h12a2 2 0 012 2 2 2 0 01-2 2H4a2 2 0 01-2-2z" />
              </svg>
            </button>
          </div>
        </header>

        <!-- Tabs para Usuários Disponíveis e Conversas -->
        <div class="flex border-b border-gray-300">
          <button
            @click="activeTab = 'users'"
            :class="[
              'flex-1 py-3 px-4 text-sm font-medium transition-colors',
              activeTab === 'users'
                ? 'text-oxford-blue border-b-2 border-oxford-blue'
                : 'text-gray-500 hover:text-gray-700'
            ]"
          >
            Usuários ({{ availableUsers.length }})
          </button>
          <button
            @click="activeTab = 'conversations'"
            :class="[
              'flex-1 py-3 px-4 text-sm font-medium transition-colors',
              activeTab === 'conversations'
                ? 'text-oxford-blue border-b-2 border-oxford-blue'
                : 'text-gray-500 hover:text-gray-700'
            ]"
          >
            Conversas ({{ activeConversations.length }})
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="p-4 text-center text-gray-500">
          <i class="pi pi-spin pi-spinner text-2xl"></i>
          <p class="mt-2">Carregando...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="p-4 text-center text-red-500">
          <i class="pi pi-exclamation-triangle text-2xl"></i>
          <p class="mt-2">{{ error }}</p>
          <button @click="retryLoad" class="mt-2 text-sm text-oxford-blue hover:underline">
            Tentar novamente
          </button>
        </div>

        <!-- Content based on active tab -->
        <div v-else class="overflow-y-auto h-screen p-3 mb-9 pb-20">
          <!-- Usuários Disponíveis -->
          <div v-if="activeTab === 'users'">
            <div
              v-for="user in availableUsers"
              :key="user.recruiter_id || user.professional_id"
              @click="startChat(user.recruiter_id || user.professional_id, user.recruiter_id ? 'recruiter' : 'professional')"
              class="flex items-center mb-4 cursor-pointer hover:bg-gray-100 p-3 rounded-md transition-colors"
            >
              <div class="w-12 h-12 bg-gray-300 rounded-full mr-3 flex-shrink-0">
                <img
                  v-if="user.photo"
                  :src="`data:image/png;base64,${user.photo}`"
                  :alt="user.name"
                  class="w-12 h-12 rounded-full object-cover"
                />
                <img
                  v-else
                  src="@/assets/images/avatar.png"
                  :alt="user.name"
                  class="w-12 h-12 rounded-full"
                />
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="text-sm font-semibold text-gray-900 truncate">{{ user.name }}</h3>
                <p class="text-xs text-gray-500 truncate">
                  {{ user.recruiter_id ? user.company_name : user.title }}
                </p>
                <p class="text-xs text-gray-400 truncate">
                  {{ user.recruiter_id ? user.position : user.city }}
                </p>
              </div>
              <div v-if="user.available_for_chat" class="w-3 h-3 bg-green-500 rounded-full"></div>
            </div>
          </div>

          <!-- Conversas Ativas -->
          <div v-else-if="activeTab === 'conversations'">
            <div
              v-for="conversation in activeConversations"
              :key="conversation.conversation_id"
              @click="openConversation(conversation)"
              class="flex items-center mb-4 cursor-pointer hover:bg-gray-100 p-3 rounded-md transition-colors"
            >
              <div class="w-12 h-12 bg-gray-300 rounded-full mr-3 flex-shrink-0">
                <img
                  src="@/assets/images/avatar.png"
                  alt="User Avatar"
                  class="w-12 h-12 rounded-full"
                />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold text-gray-900 truncate">
                    {{ conversation.other_user_name || 'Usuário' }}
                  </h3>
                  <span v-if="conversation.unread_count > 0" 
                        class="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                    {{ conversation.unread_count }}
                  </span>
                </div>
                <p class="text-xs text-gray-500 truncate">{{ conversation.last_message }}</p>
                <p class="text-xs text-gray-400 truncate">
                  {{ formatTime(conversation.last_message_time) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Chat Area -->
      <div class="flex-1">
        <!-- Chat Header -->
        <header v-if="currentChat" class="bg-white p-4 text-gray-700 border-b border-gray-300">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <button @click="closeChat" class="mr-3 text-gray-500 hover:text-gray-700">
                <i class="pi pi-arrow-left text-xl"></i>
              </button>
              <h1 class="text-xl font-semibold">
                {{ getCurrentChatUserName() }}
              </h1>
            </div>
            <div class="flex items-center space-x-2">
              <button @click="refreshChat" class="text-gray-500 hover:text-gray-700">
                <i class="pi pi-refresh text-lg"></i>
              </button>
            </div>
          </div>
        </header>

        <!-- No Chat Selected -->
        <div v-else class="flex items-center justify-center h-full bg-gray-50">
          <div class="text-center text-gray-500">
            <i class="pi pi-comments text-6xl mb-4"></i>
            <h2 class="text-xl font-semibold mb-2">Selecione uma conversa</h2>
            <p class="text-sm">Escolha um usuário ou conversa para começar a conversar</p>
          </div>
        </div>

        <!-- Chat Messages -->
        <div v-if="currentChat" class="h-screen overflow-y-auto p-4 pb-36">
          <div
            v-for="(message, index) in currentMessages"
            :key="message.id || index"
            @click="toggle($event, message)"
            class="mb-4"
          >
            <!-- Incoming Message -->
            <div
              v-if="!message.is_own"
              class="flex cursor-pointer"
            >
              <div class="w-9 h-9 rounded-full flex items-center justify-center mr-2">
                <img
                  src="@/assets/images/avatar.png"
                  alt="User Avatar"
                  class="w-8 h-8 rounded-full"
                />
              </div>
              <div class="flex max-w-96 shadow-md bg-white rounded-lg p-3 gap-3">
                <p class="text-gray-700">{{ message.message }}</p>
              </div>
            </div>

            <!-- Outgoing Message -->
            <div
              v-else
              class="flex justify-end items-center cursor-pointer"
            >
              <i @click="toggle" class="pi pi-ellipsis-h text-xl mr-5"></i>
              <div
                class="flex max-w-96 bg-oxford-blue shadow-md text-white rounded-lg p-3 gap-3"
              >
                <p>{{ message.message }}</p>
              </div>
              <div class="w-9 h-9 rounded-full flex items-center justify-center ml-2">
                <img
                  v-if="currentUserPhoto"
                  :src="currentUserPhoto"
                  alt="My Avatar"
                  class="w-8 h-8 rounded-full"
                />
                <img
                  v-else
                  src="@/assets/images/avatar.png"
                  alt="My Avatar"
                  class="w-8 h-8 rounded-full"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Chat Input -->
        <footer v-if="currentChat" class="w-3/4 bg-white border-t border-gray-300 p-4 absolute bottom-0">
          <div class="flex items-center w-full">
            <InputText
              type="text"
              id="text"
              placeholder="Escreva sua mensagem..."
              class="w-full p-2 rounded-md border border-gray-400 focus:outline-none focus:border-blue-500"
              ref="inputRef"
              v-model="text"
              @keyup.enter="handleSendMessage"
            />
            <Button
              @click="handleSendMessage"
              label="Enviar"
              class="bg-oxford-blue text-white px-4 py-2 rounded-md ml-2"
              :disabled="!text.trim() || isLoading"
            />
          </div>
        </footer>
      </div>
    </div>

    <!-- Overlay Panel for Message Actions -->
    <OverlayPanel ref="op">
      <ul>
        <li>
          <Button
            type="button"
            icon="pi pi-copy"
            label="Copiar"
            @click="copyToClipboard"
          />
        </li>
        <li>
          <Button 
            type="button" 
            icon="pi pi-reply" 
            label="Responder" 
            @click="answerMessage"
          />
        </li>
        <li>
          <Button
            @click="confirmDelete($event)"
            type="button"
            icon="pi pi-trash"
            label="Eliminar"
          />
        </li>
      </ul>
    </OverlayPanel>

    <ConfirmPopup></ConfirmPopup>
  </div>
</template>

<script setup>
import Button from "primevue/button";
import ConfirmPopup from "primevue/confirmpopup";
import InputText from "primevue/inputtext";
import OverlayPanel from "primevue/overlaypanel";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { ref, nextTick, onMounted } from "vue";
import { useChat } from "@/composables/useChat";

console.log('🚀🚀🚀 DashboardChat.vue SCRIPT SETUP EXECUTADO! 🚀🚀🚀');

const confirm = useConfirm();
const toast = useToast();
const text = ref("");
const activeTab = ref('users');
const op = ref();
const inputRef = ref();

// Usar o composable de chat
console.log('🔄🔄🔄 CHAMANDO useChat()... 🔄🔄🔄');
const {
  availableUsers,
  activeConversations,
  currentChat,
  currentMessages,
  isLoading,
  error,
  currentUserPhoto,
  loadAvailableUsers,
  loadActiveConversations,
  startChat,
  sendMessage,
  closeChat,
  markMessagesAsRead
} = useChat();

console.log('✅✅✅ useChat() EXECUTADO COM SUCESSO! ✅✅✅');
console.log('📊 Estado inicial:', {
  availableUsers: availableUsers.value,
  activeConversations: activeConversations.value,
  isLoading: isLoading.value,
  error: error.value
});

// Log adicional para confirmar que o composable está funcionando
onMounted(() => {
  console.log('🚀🚀🚀 DashboardChat.vue COMPONENTE MONTADO! 🚀🚀🚀');
  console.log('📊 Estado após montagem:', {
    availableUsers: availableUsers.value,
    activeConversations: activeConversations.value,
    isLoading: isLoading.value,
    error: error.value
  });
});

// Funções auxiliares
const toggle = (event) => {
  const messageElement = event.currentTarget.querySelector("p");
  selectedText.value = messageElement?.textContent || "";
  op.value.toggle(event);
};

const confirmDelete = (event) => {
  confirm.require({
    target: event.currentTarget,
    message: "Tem certeza que deseja excluir esta mensagem?",
    icon: "pi pi-info-circle",
    acceptClass: "p-button-danger p-button-sm",
    accept: () => {
      toast.add({
        severity: "info",
        summary: "Confirmado",
        detail: "Mensagem excluída",
        life: 3000,
      });
    },
    reject: () => {
      toast.add({
        severity: "error",
        summary: "Rejeitado",
        detail: "Operação cancelada",
        life: 3000,
      });
    },
  });
};

const copyToClipboard = () => {
  if (!selectedText.value) {
    console.warn("Nenhuma mensagem encontrada.");
    return;
  }
  
  navigator.clipboard.writeText(selectedText.value).then(() => {
    toast.add({
      severity: "info",
      summary: "Copiado",
      detail: "Texto copiado para a área de transferência",
      life: 3000,
    });
  }).catch(() => {
    // Fallback para navegadores mais antigos
    const textarea = document.createElement("textarea");
    textarea.value = selectedText.value;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    
    try {
      const sucesso = document.execCommand("copy");
      if (sucesso) {
        toast.add({
          severity: "info",
          summary: "Copiado",
          detail: "Texto copiado para a área de transferência",
          life: 3000,
        });
      }
    } catch (err) {
      console.error("Erro ao copiar:", err);
    }
    document.body.removeChild(textarea);
  });
};

const answerMessage = () => {
  if (!selectedText.value) {
    toast.add({
      severity: "warn",
      summary: "Aviso",
      detail: "Nenhuma mensagem selecionada para responder.",
      life: 3000,
    });
    return;
  }
  text.value = `RE: ${selectedText.value} `;
  nextTick(() => {
    inputRef.value?.focus();
  });
};

const handleSendMessage = async () => {
  if (!text.value.trim()) return;
  
  try {
    await sendMessage(text.value);
    text.value = "";
    nextTick(() => {
      inputRef.value?.focus();
    });
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Erro",
      detail: "Falha ao enviar mensagem. Tente novamente.",
      life: 3000,
    });
  }
};

const openConversation = async (conversation) => {
  try {
    await startChat(conversation.other_user_id, conversation.other_user_type);
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Erro",
      detail: "Falha ao abrir conversa. Tente novamente.",
      life: 3000,
    });
  }
};

const refreshChat = async () => {
  try {
    await loadActiveConversations();
    if (currentChat.value) {
      await markMessagesAsRead();
    }
    toast.add({
      severity: "success",
      summary: "Atualizado",
      detail: "Chat atualizado com sucesso",
      life: 2000,
    });
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Erro",
      detail: "Falha ao atualizar chat",
      life: 3000,
    });
  }
};

const retryLoad = async () => {
  try {
    await loadAvailableUsers();
    await loadActiveConversations();
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Erro",
      detail: "Falha ao recarregar dados",
      life: 3000,
    });
  }
};

const getCurrentChatUserName = () => {
  if (!currentChat.value) return '';
  
  // Buscar o nome do usuário na lista de usuários disponíveis
  const user = availableUsers.value.find(u => 
    (u.recruiter_id || u.professional_id) === currentChat.value.otherUserId
  );
  
  return user ? user.name : 'Usuário';
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = (now - date) / (1000 * 60 * 60);
  
  if (diffInHours < 24) {
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  } else if (diffInHours < 48) {
    return 'Ontem';
  } else {
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit' 
    });
  }
};

// Variáveis locais
const selectedText = ref("");
</script>

<style scoped></style>
